import { useState, useEffect, useRef } from 'react';
import { useStorage, type Asset } from '@/hooks/useStorage';
import { showToast } from '@/components/Toast';

interface ImagePickerProps {
  value: string;
  onChange: (url: string) => void;
  type: string;
  category?: string;
  label?: string;
}

const ALLOWED_EXT = ['.webp', '.jpg', '.jpeg', '.png'];
const ALLOWED_MIME = ['image/webp', 'image/jpeg', 'image/png'];
const MAX_SIZE_MB = 2;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

export default function ImagePicker({ value, onChange, type, category, label }: ImagePickerProps) {
  const { listAssets, uploadImage } = useStorage();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showPicker) {
      setLoading(true);
      listAssets(type, category).then((data) => {
        setAssets(data);
        setLoading(false);
      });
    }
  }, [showPicker, type, category, listAssets]);

  const hasPreview = value && value.startsWith('http');

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_MIME.includes(file.type)) {
      const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
      if (!ALLOWED_EXT.includes(ext)) {
        return `Formato não permitido. Use: ${ALLOWED_EXT.join(', ')}`;
      }
    }
    if (file.size > MAX_SIZE_BYTES) {
      return `Arquivo muito grande. Máximo ${MAX_SIZE_MB}MB.`;
    }
    return null;
  };

  const handleFile = async (file: File) => {
    const error = validateFile(file);
    if (error) {
      showToast(error, 'error');
      return;
    }
    setUploading(true);
    const asset = await uploadImage(file, type, file.name, category);
    setUploading(false);
    if (asset) {
      onChange(asset.public_url);
      showToast('Imagem enviada com sucesso!', 'success');
      // Atualiza lista se picker estiver aberto
      if (showPicker) {
        setLoading(true);
        listAssets(type, category).then((data) => {
          setAssets(data);
          setLoading(false);
        });
      }
    }
  };

  return (
    <div>
      <label className="text-brand-text-muted text-xs mb-1 block">{label || 'Imagem'}</label>
      <div className="flex items-center gap-3">
        <div className="w-16 h-12 rounded-md overflow-hidden bg-brand-darker flex-shrink-0 border border-brand-gold/10">
          {hasPreview ? (
            <img src={value} alt="Preview" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <i className="ri-image-line text-brand-text-muted text-xs" />
            </div>
          )}
        </div>
        <div className="flex-1">
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full bg-brand-dark/50 border border-brand-gold/20 rounded-md py-2 px-3 text-brand-text text-sm focus:outline-none focus:border-brand-gold/50"
            placeholder="URL da imagem (cole ou selecione)"
          />
        </div>
        <button
          onClick={() => setShowPicker(!showPicker)}
          className="flex items-center gap-2 px-3 py-2 rounded-md border border-brand-gold/20 text-brand-text-muted hover:text-white hover:bg-brand-dark/40 text-xs transition-colors cursor-pointer whitespace-nowrap"
          title="Selecionar do banco de imagens"
        >
          <i className="ri-folder-image-line" />
          Biblioteca
        </button>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 px-3 py-2 rounded-md border border-brand-gold/20 text-brand-text-muted hover:text-white hover:bg-brand-dark/40 text-xs transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          title="Enviar nova imagem"
        >
          <i className={`${uploading ? 'ri-loader-4-line animate-spin' : 'ri-upload-cloud-line'}`} />
          {uploading ? 'Enviando...' : 'Enviar'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".webp,.jpg,.jpeg,.png"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = '';
          }}
        />
      </div>

      {showPicker && (
        <div className="mt-3 bg-brand-dark/60 border border-brand-gold/20 rounded-lg p-3 max-h-60 overflow-y-auto">
          {loading && (
            <div className="text-center py-4">
              <div className="w-5 h-5 border-2 border-gray-700 border-t-amber-500 rounded-full animate-spin mx-auto mb-2" />
              <p className="text-brand-text-muted text-xs">Carregando...</p>
            </div>
          )}
          {!loading && assets.length === 0 && (
            <p className="text-brand-text-muted text-xs text-center py-4">
              Nenhuma imagem deste tipo na biblioteca.
              <br />
              Use o botão "Enviar" acima para adicionar.
            </p>
          )}
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {assets.map((asset) => (
              <button
                key={asset.id}
                onClick={() => {
                  onChange(asset.public_url);
                  setShowPicker(false);
                }}
                className={`relative aspect-video rounded-md overflow-hidden border transition-colors cursor-pointer ${
                  value === asset.public_url
                    ? 'border-brand-gold ring-1 ring-brand-gold'
                    : 'border-brand-gold/10 hover:border-brand-gold/30'
                }`}
              >
                <img src={asset.public_url} alt={asset.name} className="w-full h-full object-cover" />
                {value === asset.public_url && (
                  <div className="absolute inset-0 bg-brand-gold/20 flex items-center justify-center">
                    <i className="ri-check-line text-brand-gold text-lg" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}